import { BookmarkIconButton } from "@/components/core/inputs/BookmarkIconButton";
import { DownloadIconButton } from "@/components/core/inputs/DownloadIconButton";
import { cn } from "@/lib/utils";
import { useArchiveBookmark } from "hooks/useArchiveBookmark";
import { useAuth } from "hooks/useAuth";
import { ArchiveIconButton } from "lib/icons";
import Image from "next/image";
import { ComponentProps, useState } from "react";
import { getGoogleDriveThumbnailUrl } from "utils/googleDrive";

import { ContentCardScaffold } from "../contents/cards/scaffolds/ContentCardScaffold";
import { ArchiveTags } from "./ArchiveTags";

type ArchiveItem = {
    id: string;
    title: string;
    source: string;
    sourcename: string;
    mix_archive_sourcename?: {
        imgurl?: string | null;
        source: string;
        sourcename: string;
    } | null;
    view_url: string;
    download_url: string;
    thumbnail_url?: string | null;
    tags?: Array<string> | null;
    created_at?: any | null;
    updated_at?: any | null;
    bookmarked?: boolean;
};

// Calculate time difference for "X일전" display
const getTimeAgo = (date: any) => {
    if (!date) return "";
    const now = new Date();
    const past = new Date(date);
    const diffInDays = Math.floor(
        (now.getTime() - past.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffInDays === 0) return "오늘";
    if (diffInDays === 1) return "1일전";
    return `${diffInDays}일전`;
};

export const ArchiveHorizontalCard = ({
    archive,
    tagEnums,
    onClick,
    onDownload,
    isGuest = false,
    className,
    ...props
}: Omit<ComponentProps<typeof ContentCardScaffold>, "children"> & {
    archive: ArchiveItem;
    tagEnums: Array<{ value: string; comment?: string | null }>;
    onClick?: () => void;
    onDownload?: (e: React.MouseEvent) => void;
    isGuest?: boolean;
}) => {
    const [isDownloadClicked, setIsDownloadClicked] = useState(false);
    const { toggle: toggleBookmark } = useArchiveBookmark(archive);
    const { openSignInModal } = useAuth();

    // Generate thumbnail from view_url if thumbnail_url is not available
    const thumbnailUrl =
        archive.thumbnail_url ||
        (archive.view_url
            ? getGoogleDriveThumbnailUrl(archive.view_url, "w1920")
            : null);

    const [showFallback, setShowFallback] = useState(!thumbnailUrl);

    const handleDownload = (e: React.MouseEvent) => {
        e.stopPropagation();

        if (isGuest) {
            openSignInModal();
            return;
        }

        // Show orange color momentarily
        setIsDownloadClicked(true);
        setTimeout(() => {
            setIsDownloadClicked(false);
        }, 200);

        if (onDownload) {
            onDownload(e);
        } else {
            window.open(archive.download_url, "_blank");
        }
    };

    const handleBookmark = (e: React.MouseEvent) => {
        e.stopPropagation();
        toggleBookmark();
    };

    return (
        <ContentCardScaffold className={className} {...props} onClick={onClick}>
            <div className="mb-3 flex items-start justify-between gap-2">
                <header className="flex min-w-0 flex-1 items-center gap-2">
                    <Image
                        src={
                            archive.mix_archive_sourcename?.imgurl ||
                            "https://cdn.mix.day/img/mix.png"
                        }
                        alt={archive.sourcename}
                        width={32}
                        height={32}
                        className="rounded-sm"
                        unoptimized
                        onError={(e) => {
                            e.currentTarget.style.display = "none";
                        }}
                    />
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <div className="flex items-center gap-1">
                            <span
                                className="overflow-hidden text-ellipsis
                                    whitespace-nowrap text-[13px] font-semibold
                                    text-text-primary"
                            >
                                {archive.sourcename}
                            </span>
                        </div>
                        {archive.created_at && (
                            <span
                                className="shrink-0 text-xs text-text-secondary"
                            >
                                {getTimeAgo(archive.created_at)}
                            </span>
                        )}
                    </div>
                </header>
                <div className="flex items-center gap-2">
                    <div
                        onClick={handleDownload}
                        className="animate-button-slide-in-delay-1
                            cursor-pointer opacity-0"
                    >
                        <DownloadIconButton
                            variant="icon"
                            checked={isDownloadClicked}
                            onChange={() => {}}
                        />
                    </div>
                    <div
                        onClick={handleBookmark}
                        className="animate-button-slide-in-delay-2
                            cursor-pointer opacity-0"
                    >
                        <BookmarkIconButton
                            variant="icon"
                            checked={archive.bookmarked || false}
                            onChange={() => {}}
                        />
                    </div>
                </div>
            </div>

            <div className="mb-2.5 flex gap-3">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <ContentCardScaffold.Title
                        className="line-clamp-2 text-base font-semibold
                            leading-[1.4]"
                    >
                        {archive.title}
                    </ContentCardScaffold.Title>
                </div>
                <div className="relative h-[70px] w-[70px] shrink-0">
                    <ContentCardScaffold.ThumbnailImg
                        src={thumbnailUrl ?? ""}
                        unoptimized
                        fill={true}
                        onFallbackChange={setShowFallback}
                    />
                </div>
            </div>

            <div className="flex items-center justify-between gap-2">
                {archive.tags && archive.tags.length > 0 ? (
                    <ArchiveTags tags={archive.tags} tagEnums={tagEnums} />
                ) : (
                    <div />
                )}
                <ArchiveIconButton />
            </div>
        </ContentCardScaffold>
    );
};
